import { Divider, FieldTypes, Heading, RecordContent, RepeatableSection } from '@msaf/core-react'
import {
  CasingFieldsEdit,
  DecommissioningFieldsEdit,
  FormFieldsEdit,
  GalleryDetailsFieldsEdit,
  ScreenFieldsEdit,
} from '../../../../types/form-types'
import { useWellState } from '../../../context/well-context'
import { AddField, RemoveField, SetValue, ValidationErrors } from '../../../hooks/use-edit-form'
import WellConstructionCasingsEdit from './casings-edit'
import WellConstructionDecommissioningEdit from './decommissioning-edit'
import WellConstructionGalleryDetailsEdit from './gallery-details-edit'
import WellConstructionScreensEdit from './screens-edit'
import WellHeadConstructionEdit from './well-head-construction-edit'

interface EditWellConstructionFormProps {
  isLoading: boolean
  fields: Partial<FormFieldsEdit>
  addField: AddField<FormFieldsEdit>
  setValue: SetValue<FormFieldsEdit>
  removeField: RemoveField<FormFieldsEdit>
  isNewRecord?: boolean
  validationErrors?: ValidationErrors
  isWellDecommissioned?: boolean
}
export function EditWellConstructionForm({
  isLoading,
  fields,
  addField,
  removeField,
  setValue,
  validationErrors = {},
}: EditWellConstructionFormProps) {
  const {
    wellState: { isDecommissionedWell },
  } = useWellState()
  return (
    <RecordContent>
      <Heading isSkeleton={isLoading} level={2}>
        Construction
      </Heading>
      {fields.galleryDetails ? (
        <RepeatableSection
          heading='Gallery details'
          sectionName='Gallery'
          removeField={(index: number) => removeField('galleryDetails', index)}
          addField={() => addField('galleryDetails', {})}
          isSkeleton={isLoading}
        >
          {fields.galleryDetails.map((gallery: Partial<GalleryDetailsFieldsEdit>, index) => (
            <WellConstructionGalleryDetailsEdit
              key={gallery.id}
              fields={gallery}
              index={index}
              isLoading={isLoading}
              setValue={(key: keyof GalleryDetailsFieldsEdit, value: FieldTypes) =>
                setValue('galleryDetails', value, key, index)
              }
              validationErrors={validationErrors?.galleryDetails?.[index] as ValidationErrors}
            />
          ))}
        </RepeatableSection>
      ) : (
        <>
          <RepeatableSection
            heading='Casing details'
            sectionName='Casing'
            removeField={(index: number) => removeField('casings', index)}
            addField={() => addField('casings', {})}
            isSkeleton={isLoading}
          >
            {fields.casings?.map((casing: Partial<CasingFieldsEdit>, index) => (
              <WellConstructionCasingsEdit
                key={casing.id}
                fields={casing}
                index={index}
                isLoading={isLoading}
                setValue={(key: keyof CasingFieldsEdit, val: FieldTypes) => setValue('casings', val, key, index)}
                validationErrors={validationErrors?.casings?.[index] as ValidationErrors}
              />
            ))}
          </RepeatableSection>

          <Divider />

          <RepeatableSection
            heading='Screen details'
            isSkeleton={isLoading}
            sectionName='Screen'
            removeField={(index: number) => removeField('screens', index)}
            addField={() => addField('screens', {})}
          >
            {fields.screens?.map((screen: Partial<ScreenFieldsEdit>, index) => (
              <WellConstructionScreensEdit
                key={screen.id}
                fields={screen}
                index={index}
                isLoading={isLoading}
                setValue={(key: keyof ScreenFieldsEdit, value: FieldTypes) => setValue('screens', value, key, index)}
                validationErrors={validationErrors?.screens?.[index] as ValidationErrors}
              />
            ))}
          </RepeatableSection>
        </>
      )}

      <Divider />

      <Heading isSkeleton={isLoading} level={3}>
        Well head construction
      </Heading>

      {fields && (
        <WellHeadConstructionEdit
          validationErrors={validationErrors ?? {}}
          fields={fields}
          isLoading={isLoading}
          setValue={setValue}
        />
      )}

      {isDecommissionedWell && (
        <>
          <Divider />

          <Heading isSkeleton={isLoading} level={3}>
            Well decommissioning
          </Heading>

          {!Array.isArray(fields.decommissioning) && typeof fields.decommissioning === 'object' && (
            <WellConstructionDecommissioningEdit
              fields={{ ...fields.decommissioning, councilId: fields.council } ?? {}}
              isLoading={isLoading}
              validationErrors={validationErrors ?? {}}
              setValue={(key: keyof DecommissioningFieldsEdit, value: FieldTypes) =>
                setValue('decommissioning', value, key)
              }
            />
          )}
        </>
      )}
    </RecordContent>
  )
}
